import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Email from './Email';
import Register from './Register';
import Verify from './Verify';
import Preferences from './Preferences';
import { validateLogin, verifyEmail, sendPin, getSessionToken } from '../Services/services';
import { setAuth, setNeedsSurvey, setEmail, setLoading, setError, clearAuth, setEngagementStatus } from '../store';
import { useBranding } from '../Context/BrandingContext';
import {
  EMAIL_SUBSYSTEM_USER_MESSAGE,
  isRejectedEngagementStatus,
  SUSPENDED_ACCOUNT_MESSAGE,
} from '../constants/authMessages';

// Auth flow states
const STATES = {
  LOADING: 'loading',
  EMAIL: 'email',
  REGISTER: 'register',
  PREFERENCES: 'preferences',
  VERIFY: 'verify',
};

function Main({ variant = 'page', redirectPath, onClose, onCancel }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading } = useSelector((state) => state.auth);
  const [currentState, setCurrentState] = useState(STATES.LOADING);
  const [verifyInitialError, setVerifyInitialError] = useState(null);
  const hasFetched = useRef(false);
  const { branding } = useBranding();

  // Called by Cancel buttons inside the form — navigates home and closes the modal.
  const closeAndReset = () => {
    dispatch(clearAuth());
    setCurrentState(STATES.EMAIL);
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  // Get redirect URL from query params (supports 'redirect' or 'r') or explicit redirectPath (modal)
  const getRedirectUrl = () => {
    if (typeof redirectPath === 'string' && redirectPath.startsWith('/')) {
      return redirectPath;
    }
    const redirect = searchParams.get('redirect') || searchParams.get('r');
    // Validate it's a relative path (security: prevent open redirects)
    if (redirect && redirect.startsWith('/')) {
      return redirect;
    }
    return '/';
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExistingSession = async () => {
    dispatch(setLoading(true));
    
    // Quick check - if we have a session token, user is already logged in
    if (getSessionToken()) {
      navigate(getRedirectUrl(), { replace: true });
      if (variant === 'modal' && onClose) onClose();
      return;
    }
    
    try {
      // Check if user has an existing session via API
      const response = await validateLogin();
      const data = response.data;

      if (data.status === 1 && data.data) {
        // Session exists - redirect to original destination or home
        dispatch(setAuth({
          email: data.data.email,
          role: data.data.role,
          isSupportRep: data.data.isSupportRep,
        }));
        navigate(getRedirectUrl(), { replace: true });
        if (variant === 'modal' && onClose) onClose();
      } else {
        // No session, show email input
        setCurrentState(STATES.EMAIL);
      }
    } catch (err) {
      console.log('No existing session:', err);
      setCurrentState(STATES.EMAIL);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEmailSubmit = async (userEmail, checkResult) => {
    dispatch(setEmail(userEmail));
    dispatch(setLoading(true));
    
    try {
      // If checkResult is passed from Email component (new flow)
      if (checkResult !== undefined) {
        // Handle not found - show registration
        if (checkResult.status === 404 || checkResult.notFound) {
          setCurrentState(STATES.REGISTER);
          return;
        }

        // Handle rejected / suspended status (defense in depth if Email did not block)
        if (isRejectedEngagementStatus(checkResult.engagementStatus)) {
          dispatch(setError(checkResult.statusMessage || SUSPENDED_ACCOUNT_MESSAGE));
          setCurrentState(STATES.EMAIL);
          return;
        }

        // User found - store engagement status and send PIN
        dispatch(setEngagementStatus(checkResult.engagementStatus));
        
        // Send PIN code. If the email subsystem is down, advance to Verify
        // anyway with the notice — never leave the user on the email screen.
        let pinEmailError = null;
        try {
          const pinResponse = await sendPin(userEmail);
          if (!pinResponse.data?.success) {
            if (pinResponse.status === 422) {
              dispatch(setError(SUSPENDED_ACCOUNT_MESSAGE));
              setCurrentState(STATES.EMAIL);
              return;
            }
            pinEmailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
          }
        } catch (pinError) {
          if (pinError.response?.status === 422) {
            dispatch(setError(SUSPENDED_ACCOUNT_MESSAGE));
            setCurrentState(STATES.EMAIL);
            return;
          }
          console.error('Failed to send PIN:', pinError);
          pinEmailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
        }
        setVerifyInitialError(pinEmailError);
        setCurrentState(STATES.VERIFY);
        return;
      }

      // Legacy flow - fallback to verifyEmail for backwards compatibility
      const response = await verifyEmail(userEmail, window.location.href);
      const data = response.data;

      if (data.status !== 1) {
        dispatch(setError(data.message || 'Verification failed'));
        return;
      }

      // Check if user needs to complete survey (new user without seniority)
      if (data.data?.portalEng && !data.data.portalEng.seniority) {
        dispatch(setNeedsSurvey(data.data));
        setCurrentState(STATES.PREFERENCES);
        return;
      }

      // Existing user, proceed to PIN verification
      setCurrentState(STATES.VERIFY);
    } catch (err) {
      console.error('Email verification error:', err);
      dispatch(setError('Verification failed. Please try again.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRegisterComplete = (emailSubsystemError) => {
    setVerifyInitialError(emailSubsystemError || null);
    setCurrentState(STATES.VERIFY);
  };

  const handlePreferencesComplete = (emailSubsystemError) => {
    setVerifyInitialError(emailSubsystemError || null);
    setCurrentState(STATES.VERIFY);
  };

  const handleVerifyComplete = (authData) => {
    dispatch(setAuth(authData));
    // Redirect to original destination or home after successful login
    navigate(getRedirectUrl(), { replace: true });
    if (variant === 'modal' && onClose) onClose();
  };

  const handleBack = () => {
    setCurrentState(STATES.EMAIL);
    dispatch(clearAuth());
  };

  // Loading state
  if (currentState === STATES.LOADING || loading) {
    const spinnerStyle = {
      ...styles.spinner,
      borderTop: `4px solid ${branding.primaryColor}`,
    };
    return (
      <div style={styles.loadingContainer}>
        <div style={spinnerStyle}></div>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Render based on current state
  switch (currentState) {
    case STATES.EMAIL:
      return <Email variant={variant} onEmailSubmit={handleEmailSubmit} onCancel={variant === 'modal' ? closeAndReset : undefined} />;
    
    case STATES.REGISTER:
      return (
        <Register
          variant={variant}
          onRegisterComplete={handleRegisterComplete}
          onBack={handleBack}
          onCancel={variant === 'modal' ? closeAndReset : undefined}
        />
      );
    
    case STATES.PREFERENCES:
      return (
        <Preferences
          variant={variant}
          onComplete={handlePreferencesComplete}
          onBack={handleBack}
          onCancel={variant === 'modal' ? closeAndReset : undefined}
        />
      );
    
    case STATES.VERIFY:
      return (
        <Verify
          variant={variant}
          onVerifyComplete={handleVerifyComplete}
          onBack={handleBack}
          onCancel={variant === 'modal' ? closeAndReset : undefined}
          initialError={verifyInitialError}
        />
      );
    
    default:
      return <Email variant={variant} onEmailSubmit={handleEmailSubmit} onCancel={variant === 'modal' ? closeAndReset : undefined} />;
  }
}

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #0066cc',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#666',
    fontSize: '16px',
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default Main;
